import { STATUS_DONE, STATUS_LIVE } from "../components/bondhouse/FixtureStatus";
import { getFixtureMarketCount } from "./bondhouseHelpers";
import {
  formatPeriod,
  formatSelection,
  marketTitle,
  shortTeam,
} from "./joinedBetsHelpers";
import { formatAppDateTime } from "./appTimeFormat";
import { sortLeaguesByPriority } from "./leaguePriority";

export const PLACE_BET_PATH = (houseId) => `/app/bethouse/${houseId}/place`;

export const PLACE_BET_FIXTURE_PATH = (houseId, fixtureId) => {
  const base = PLACE_BET_PATH(houseId);
  if (!fixtureId) return base;
  return `${base}?fixture=${encodeURIComponent(String(fixtureId))}`;
};

export const fmtOdd = (n) => {
  const v = Number(n);
  if (!v || v <= 1) return "—";
  return v.toFixed(2);
};

export const buildOddPick = (section, subSection, key, value) => ({
  section,
  subSection: subSection || null,
  key,
  value: Number(value),
});

export const flattenOddsMarkets = (odds) => {
  if (!odds || typeof odds !== "object") return [];
  const rows = [];

  for (const [section, sectionVal] of Object.entries(odds)) {
    if (!sectionVal || typeof sectionVal !== "object") continue;

    const firstVal = Object.values(sectionVal)[0];
    if (typeof firstVal === "number" || typeof firstVal === "string") {
      const value = Number(firstVal);
      if (value > 1) rows.push(buildOddPick(section, null, section, value));
      continue;
    }

    for (const [subSection, keys] of Object.entries(sectionVal)) {
      if (!keys || typeof keys !== "object") continue;
      for (const [key, rawVal] of Object.entries(keys)) {
        const value = Number(rawVal);
        if (value > 1) rows.push(buildOddPick(section, subSection, key, value));
      }
    }
  }

  return rows;
};

export const groupOddsMarkets = (rows = []) => {
  const map = new Map();
  for (const row of rows) {
    const groupKey = `${row.section}::${row.subSection || ""}`;
    if (!map.has(groupKey)) {
      map.set(groupKey, {
        section: row.section,
        subSection: row.subSection,
        picks: [],
      });
    }
    map.get(groupKey).picks.push(row);
  }
  return [...map.values()];
};

const ODDS_SECTION_ORDER = [
  "oneXTwo",
  "doubleChance",
  "bothTeamsToScore",
  "overUnderGoals",
  "drawNoBet",
  "halfTimeFullTime",
  "oneXTwoAndOverUnder",
  "correctScore",
  "totalGoalsExact",
  "oddEven",
  "teamToScore",
  "halfWithMoreGoals",
  "winToNil",
  "cleanSheet",
  "doubleChanceAndBothTeamsToScore",
];

export const marketGroupKey = (group) =>
  `${group.section}::${group.subSection || ""}`;

export const formatMarketGroupTitle = (group) =>
  formatOddPickMeta({ section: group?.section, subSection: group?.subSection });

export const sortOddsMarketGroups = (groups = []) => {
  const rankOf = (section) => {
    const index = ODDS_SECTION_ORDER.indexOf(section);
    return index === -1 ? 999 : index;
  };

  return [...groups]
    .map((group) => ({
      ...group,
      picks: [...group.picks].sort((a, b) => String(a.key).localeCompare(String(b.key))),
    }))
    .sort((a, b) => {
      const rankDiff = rankOf(a.section) - rankOf(b.section);
      if (rankDiff !== 0) return rankDiff;
      return String(a.subSection || "").localeCompare(String(b.subSection || ""));
    });
};

export const buildFixtureMarketGroups = (fixture) =>
  sortOddsMarketGroups(groupOddsMarkets(flattenOddsMarkets(fixture?.odds)));

export const countMarketPicks = (groups = []) =>
  groups.reduce((total, group) => total + group.picks.length, 0);

export const filterOddsMarketGroups = (groups = [], query = "") => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return groups;

  return groups
    .map((group) => {
      const title = formatMarketGroupTitle(group).toLowerCase();
      const picks = group.picks.filter((pick) => {
        const blob = [
          title,
          formatOddPickLabel(pick),
          formatOddPickMeta(pick),
          pick.key,
        ]
          .join(" ")
          .toLowerCase();
        return blob.includes(q);
      });
      return { ...group, picks };
    })
    .filter((group) => group.picks.length > 0);
};

export const marketGridClassForGroup = (group) => {
  const count = group?.picks?.length || 0;
  if (count === 2) return "pbd-market-grid-2";
  if (count === 3) return "pbd-market-grid-3";
  return "";
};

export const formatOddPickLabel = (pick) => {
  if (!pick) return "—";
  return formatSelection(pick.section, pick.key);
};

export const formatOddPickMeta = (pick) => {
  if (!pick) return "";
  const parts = [marketTitle(pick.section)];
  if (pick.subSection) parts.push(formatPeriod(pick.subSection));
  return parts.filter(Boolean).join(" · ");
};

export const oddPickKey = (pick) =>
  pick
    ? [pick.section, pick.subSection || "-", pick.key, pick.value].join("::")
    : "";

export const isSameOddPick = (a, b) =>
  a &&
  b &&
  a.section === b.section &&
  (a.subSection || null) === (b.subSection || null) &&
  a.key === b.key;

export const isFixtureBettable = (fixture) => {
  if (!fixture?._id) return false;
  if (fixture.suspended) return false;
  const short = (fixture.status?.short || "").toUpperCase();
  if (STATUS_LIVE.has(short) || STATUS_DONE.has(short)) return false;
  if (fixture.date) {
    const kick = new Date(fixture.date).getTime();
    if (!Number.isNaN(kick) && kick <= Date.now()) return false;
  }
  return true;
};

export const getOneXTwoPicks = (fixture) => {
  const block =
    fixture?.odds?.oneXTwo?.fullTime ||
    fixture?.odds?.oneXTwo ||
    null;
  if (!block) return null;

  const home = Number(block["1"] ?? block.home);
  const draw = Number(block["X"] ?? block.draw);
  const away = Number(block["2"] ?? block.away);
  if (!home && !draw && !away) return null;

  return {
    home: home > 1 ? buildOddPick("oneXTwo", "fullTime", "1", home) : null,
    draw: draw > 1 ? buildOddPick("oneXTwo", "fullTime", "X", draw) : null,
    away: away > 1 ? buildOddPick("oneXTwo", "fullTime", "2", away) : null,
  };
};

export const fixtureIdOf = (fx) => {
  if (!fx) return null;
  if (typeof fx === "string") return fx;
  return fx._id || fx.fixture?._id || fx.fixture || null;
};

export const slipFixtureId = (leg) => fixtureIdOf(leg?.fixture);

export const findSlipLeg = (slip, fixtureId) => {
  const id = String(fixtureId);
  return (slip?.fixtures || []).find((leg) => String(slipFixtureId(leg)) === id) || null;
};

export const combinedOdds = (slip) => {
  const legs = slip?.fixtures || [];
  if (!legs.length) return 1;
  return legs.reduce((acc, leg) => acc * Number(leg?.selectedOdd?.value || leg?.value || 1), 1);
};

export const potentialWin = (stake, slip) => {
  const s = Number(stake);
  if (!s || s <= 0) return 0;
  return s * combinedOdds(slip);
};

export const minSelectionsFor = (house) =>
  Math.max(1, Number(house?.minSelectionsPerBet || house?.minFixture || 2));

export const minStakeFor = (house) =>
  Number(house?.defaultMinBetAmount || house?.minBetAmount || 0);

export const maxStakeFor = (house) =>
  Number(house?.defaultMaxBetAmount || house?.maxBetAmount || 0);

export const filterBettableFixtures = (fixtures = []) =>
  fixtures.filter(isFixtureBettable);

export const fixtureKickoffMs = (fixture) => {
  const raw = fixture?.timestamp;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return n > 1e12 ? n : n * 1000;
  if (fixture?.date) {
    const ms = new Date(fixture.date).getTime();
    if (Number.isFinite(ms) && !Number.isNaN(ms)) return ms;
  }
  return null;
};

const startOfDayMs = (ms = Date.now()) => {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

export const fixtureTimeBucket = (fixture, nowMs = Date.now()) => {
  const ts = fixtureKickoffMs(fixture);
  if (!ts) return "unknown";
  const today = startOfDayMs(nowMs);
  const tomorrow = today + 86400000;
  const weekEnd = today + 7 * 86400000;
  const fixDay = startOfDayMs(ts);
  if (fixDay <= today) return "today";
  if (fixDay < tomorrow + 86400000) return "tomorrow";
  if (fixDay < weekEnd) return "week";
  return "later";
};

export const matchesTimeFilter = (fixture, filterId, nowMs = Date.now()) => {
  if (!filterId || filterId === "all") return true;
  const bucket = fixtureTimeBucket(fixture, nowMs);
  if (filterId === "today") return bucket === "today";
  if (filterId === "tomorrow") return bucket === "tomorrow";
  if (filterId === "week") return bucket === "week" || bucket === "today" || bucket === "tomorrow";
  if (filterId === "later") return bucket === "later" || bucket === "unknown";
  return true;
};

export const leagueIdOf = (fixture) => {
  const league = fixture?.league;
  const id = league?._id || league?.id;
  if (id) return String(id);
  if (league?.name) return `name:${league.name}`;
  return null;
};

export const extractFixtureLeagues = (fixtures = []) => {
  const map = new Map();
  for (const fx of fixtures) {
    const id = leagueIdOf(fx);
    if (!id) continue;
    const prev = map.get(id);
    map.set(id, {
      id,
      apiId: fx.league?.id ?? null,
      name: fx.league?.name || "League",
      logo: fx.league?.logo || null,
      count: (prev?.count || 0) + 1,
    });
  }
  return sortLeaguesByPriority([...map.values()]);
};

export const isStartingSoon = (fixture, hours = 6, nowMs = Date.now()) => {
  const ts = fixtureKickoffMs(fixture);
  if (!ts) return false;
  return ts > nowMs && ts <= nowMs + hours * 3600000;
};

export const formatStartingSoonCountdown = (kickoffMs, nowMs = Date.now()) => {
  const ts = Number(kickoffMs);
  if (!Number.isFinite(ts) || ts <= 0) return "TBD";
  const diff = ts - nowMs;
  if (diff <= 0) return "Now";
  const totalMins = Math.floor(diff / 60000);
  if (totalMins < 60) return `${totalMins}m`;
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
};

export const formatKickOffInParts = (kickoffMs, nowMs = Date.now()) => {
  const time = formatStartingSoonCountdown(kickoffMs, nowMs);
  if (time === "TBD") return { prefix: "Kick off", time: "TBD" };
  if (time === "Now") return { prefix: "Kick off", time: "now" };
  return { prefix: "Kick off in", time };
};

export const groupStartingSoonFixtures = (fixtures = [], hours = 6, nowMs = Date.now()) => {
  const soon = fixtures
    .filter((fx) => isStartingSoon(fx, hours, nowMs))
    .sort((a, b) => (fixtureKickoffMs(a) || 0) - (fixtureKickoffMs(b) || 0));

  const groups = [];
  const bucketMap = new Map();

  for (const fixture of soon) {
    const ts = fixtureKickoffMs(fixture);
    const bucket = ts ? Math.floor(ts / 60000) * 60000 : 0;
    if (!bucketMap.has(bucket)) {
      const group = { kickoffMs: bucket, fixtures: [] };
      bucketMap.set(bucket, group);
      groups.push(group);
    }
    bucketMap.get(bucket).fixtures.push(fixture);
  }

  return groups;
};

export const flattenStartingSoonViews = (groups = []) => {
  const views = [];
  groups.forEach((group, groupIndex) => {
    group.fixtures.forEach((fixture, slotIndex) => {
      views.push({
        fixture,
        kickoffMs: group.kickoffMs,
        slotIndex,
        slotSize: group.fixtures.length,
        groupIndex,
      });
    });
  });
  return views;
};

export const PROGRAM_TIME_FILTERS = [
  { id: "all", label: "All" },
  { id: "today", label: "Today" },
  { id: "tomorrow", label: "Tomorrow" },
  { id: "week", label: "This week" },
  { id: "later", label: "Later" },
];

export const PROGRAM_SORT_OPTIONS = [
  { id: "kickoff", label: "Kickoff" },
  { id: "league", label: "League A–Z" },
  { id: "marketsDesc", label: "Most markets" },
  { id: "bettorsDesc", label: "Most bettors" },
];

export const fixtureBettorsCount = (fixture) => Math.max(0, Number(fixture?.bettorsCount) || 0);

export const fixtureMarketsCount = (fixture) => getFixtureMarketCount(fixture);

export const computeDeepMarketsThreshold = (fixtures = [], minFloor = 8) => {
  const counts = fixtures
    .map(fixtureMarketsCount)
    .filter((n) => n > 0)
    .sort((a, b) => a - b);
  if (!counts.length) return minFloor;
  const p75 = counts[Math.floor((counts.length - 1) * 0.75)] ?? counts[counts.length - 1];
  return Math.max(minFloor, p75);
};

export const countFixturesWithBettors = (fixtures = []) =>
  fixtures.filter((fx) => fixtureBettorsCount(fx) > 0).length;

export const countDeepBoardFixtures = (fixtures = [], threshold = 8) =>
  fixtures.filter((fx) => fixtureMarketsCount(fx) >= threshold).length;

const sortProgramFixtures = (list, sort) => {
  const sorted = [...list];
  if (sort === "league") {
    sorted.sort((a, b) => {
      const la = a.league?.name || "";
      const lb = b.league?.name || "";
      const cmp = la.localeCompare(lb);
      if (cmp !== 0) return cmp;
      return (fixtureKickoffMs(a) || 0) - (fixtureKickoffMs(b) || 0);
    });
    return sorted;
  }

  if (sort === "marketsDesc") {
    sorted.sort((a, b) => {
      const diff = fixtureMarketsCount(b) - fixtureMarketsCount(a);
      if (diff !== 0) return diff;
      return (fixtureKickoffMs(a) || Number.MAX_SAFE_INTEGER) - (fixtureKickoffMs(b) || Number.MAX_SAFE_INTEGER);
    });
    return sorted;
  }

  if (sort === "bettorsDesc") {
    sorted.sort((a, b) => {
      const diff = fixtureBettorsCount(b) - fixtureBettorsCount(a);
      if (diff !== 0) return diff;
      const marketDiff = fixtureMarketsCount(b) - fixtureMarketsCount(a);
      if (marketDiff !== 0) return marketDiff;
      return (fixtureKickoffMs(a) || Number.MAX_SAFE_INTEGER) - (fixtureKickoffMs(b) || Number.MAX_SAFE_INTEGER);
    });
    return sorted;
  }

  sorted.sort(
    (a, b) => (fixtureKickoffMs(a) || Number.MAX_SAFE_INTEGER) - (fixtureKickoffMs(b) || Number.MAX_SAFE_INTEGER)
  );
  return sorted;
};

export const countFixturesByTimeFilter = (fixtures = [], nowMs = Date.now()) => {
  const counts = { all: fixtures.length };
  for (const { id } of PROGRAM_TIME_FILTERS) {
    if (id === "all") continue;
    counts[id] = fixtures.filter((fx) => matchesTimeFilter(fx, id, nowMs)).length;
  }
  return counts;
};

export const fixtureMatchesSearch = (fixture, query) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const blob = [
    fixture.league?.name,
    fixture.teams?.home?.name,
    fixture.teams?.away?.name,
    formatFixtureMatchup(fixture),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return blob.includes(q);
};

export const filterProgramFixtures = (fixtures = [], options = {}) => {
  const {
    search = "",
    leagueId = "",
    timeFilter = "all",
    onSlipOnly = false,
    startingSoon = false,
    popularOnly = false,
    deepBoardOnly = false,
    deepMarketsThreshold = 8,
    sort = "kickoff",
    slip = null,
    nowMs = Date.now(),
  } = options;

  let list = fixtures.filter((fx) => fixtureMatchesSearch(fx, search));

  if (leagueId) {
    list = list.filter((fx) => leagueIdOf(fx) === leagueId);
  }

  if (timeFilter !== "all") {
    list = list.filter((fx) => matchesTimeFilter(fx, timeFilter, nowMs));
  }

  if (onSlipOnly && slip) {
    list = list.filter((fx) => findSlipLeg(slip, fx._id));
  }

  if (startingSoon) {
    list = list.filter((fx) => isStartingSoon(fx, 6, nowMs));
  }

  if (popularOnly) {
    list = list.filter((fx) => fixtureBettorsCount(fx) > 0);
  }

  if (deepBoardOnly) {
    list = list.filter((fx) => fixtureMarketsCount(fx) >= deepMarketsThreshold);
  }

  return sortProgramFixtures(list, sort);
};

export const hasActiveProgramFilters = ({
  search = "",
  leagueId = "",
  timeFilter = "all",
  onSlipOnly = false,
  startingSoon = false,
  popularOnly = false,
  deepBoardOnly = false,
  sort = "kickoff",
} = {}) =>
  Boolean(
    search.trim() ||
      leagueId ||
      timeFilter !== "all" ||
      onSlipOnly ||
      startingSoon ||
      popularOnly ||
      deepBoardOnly ||
      sort !== "kickoff"
  );

export const formatFixtureMatchup = (fixture) => {
  const home = shortTeam(fixture?.teams?.home?.name);
  const away = shortTeam(fixture?.teams?.away?.name);
  return `${home} vs ${away}`;
};

export const formatKickoffShort = (fixture) => {
  if (!fixture?.date || Number.isNaN(new Date(fixture.date).getTime())) return "TBD";
  return formatAppDateTime(fixture.date);
};

export const unwrapSlipResponse = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  if (Object.prototype.hasOwnProperty.call(payload, "betting")) return payload.betting;
  if (payload.data === null || payload.data === undefined) {
    if (Array.isArray(payload.fixtures)) return payload;
    return null;
  }
  if (Array.isArray(payload.fixtures)) return payload;
  return null;
};
